export type DraftStatus = 'saved' | 'unsaved' | 'saving' | 'offline' | 'error';
export interface DraftSnapshot {
  answers: Record<string, number>;
  status: DraftStatus;
  storageAvailable: boolean;
  restored: boolean;
}
interface Options {
  userId: string;
  attemptId: string;
  questionIds: string[];
  serverAnswers: Record<string, number>;
  save: (answers: Array<{ questionId: string; value: number }>) => Promise<unknown>;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  online?: () => boolean;
}

/** One serialized writer per open attempt. Unacknowledged values survive reloads. */
export class AssessmentDraftQueue {
  readonly key: string;
  private allowed: Set<string>;
  private pending = new Map<string, number>();
  private answers: Record<string, number> = {};
  private listeners = new Set<() => void>();
  private request: Promise<void> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private storageAvailable = true;
  private restored = false;
  private status: DraftStatus = 'saved';
  private current: DraftSnapshot;

  constructor(private options: Options) {
    this.key = `assessment-draft:v1:${encodeURIComponent(options.userId)}:${encodeURIComponent(options.attemptId)}`;
    this.allowed = new Set(options.questionIds);
    Object.entries(options.serverAnswers).forEach(([id, value]) => { if (this.valid(id, value)) this.answers[id] = value; });
    try {
      const raw = options.storage?.getItem(this.key);
      const saved = raw ? JSON.parse(raw) : null;
      if (saved?.version === 1 && saved.pending && typeof saved.pending === 'object') {
        Object.entries(saved.pending).forEach(([id, value]) => {
          if (this.valid(id, value)) { this.pending.set(id, value as number); this.answers[id] = value as number; this.restored = true; }
        });
      }
    } catch { this.storageAvailable = false; }
    if (this.pending.size) this.status = this.isOnline() ? 'unsaved' : 'offline';
    this.current = this.snapshot();
  }

  private valid(id: string, value: unknown): value is number {
    return this.allowed.has(id) && typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
  }
  private isOnline() { return this.options.online?.() !== false; }
  private snapshot(): DraftSnapshot { return { answers: { ...this.answers }, status: this.status, storageAvailable: this.storageAvailable, restored: this.restored }; }
  private emit() { this.current = this.snapshot(); this.listeners.forEach(listener => listener()); }
  private persist() {
    try {
      this.options.storage?.setItem(this.key, JSON.stringify({ version: 1, pending: Object.fromEntries(this.pending), updatedAt: new Date().toISOString() }));
      this.storageAvailable = true;
    } catch { this.storageAvailable = false; }
  }
  getSnapshot = () => this.current;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };

  answer(id: string, value: number) {
    if (this.disposed || !this.valid(id, value)) return;
    this.answers[id] = value;
    this.pending.set(id, value);
    this.status = this.isOnline() ? 'unsaved' : 'offline';
    this.persist(); // Write before the debounce and before updating the rendered answer.
    this.emit();
    this.schedule();
  }
  schedule() {
    if (this.timer) clearTimeout(this.timer);
    if (this.disposed || !this.pending.size) return;
    this.timer = setTimeout(() => { this.timer = null; void this.flush().catch(() => {}); }, 700);
  }
  connectionChanged() {
    if (!this.isOnline()) { this.status = 'offline'; this.emit(); return; }
    if (this.pending.size) void this.flush().catch(() => {});
    else { this.status = 'saved'; this.emit(); }
  }
  async flush(): Promise<void> {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    if (this.request) return this.request;
    if (!this.pending.size) return;
    this.request = this.drain();
    try { await this.request; } finally { this.request = null; }
  }
  private async drain() {
    while (this.pending.size) {
      if (!this.isOnline()) { this.status = 'offline'; this.emit(); throw new Error('offline'); }
      const batch = Array.from(this.pending, ([questionId, value]) => ({ questionId, value }));
      this.status = 'saving'; this.emit();
      try {
        await this.options.save(batch);
      } catch (error) {
        // The map still contains the newest values, including edits made during this request.
        if (!this.disposed) { this.status = this.isOnline() ? 'error' : 'offline'; this.persist(); this.emit(); }
        throw error;
      }
      // A replacement page owns this storage key now; a late response must not erase its edits.
      if (this.disposed) return;
      batch.forEach(({ questionId, value }) => { if (this.pending.get(questionId) === value) this.pending.delete(questionId); });
      this.persist();
      this.status = this.pending.size ? 'unsaved' : 'saved'; this.emit();
    }
  }
  complete() {
    try { this.options.storage?.removeItem(this.key); } catch { /* A completed attempt is never restored as an open draft. */ }
  }
  dispose() { this.disposed = true; if (this.timer) clearTimeout(this.timer); this.listeners.clear(); }
}
